import React, { useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import MuiLink from '@material-ui/core/Link';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import LaunchIcon from '@material-ui/icons/Launch';
import AddIcon from '@material-ui/icons/Add';
import Divider from '@material-ui/core/Divider';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import Button from 'components/common/Button';
import withConfirm from 'hooks/withConfirm';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { PROMPTS_CATEGORIES } from 'constant/essay';
import useSnack from 'hooks/useSnack';
import AddDocModal from './AddEssayModal';
import DocumentItem from '../../essays/cell';
import StatusSelect from '../../essays/status';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
  },
  text: {
    marginBottom: theme.spacing(3),
  },
  infoIcon: {
    display: 'block',
    fontSize: 70,
    opacity: 0.7,
    marginBottom: theme.spacing(2),
  },
  actions: {
    padding: theme.spacing(2),
    justifyContent: 'center',
  },
}));

const StatementDetails = ({ onClose, prompt, statement }) => {
  const classes = useStyles();
  return (
    <Dialog open fullWidth maxWidth="sm">
      <DialogContent>
        <InfoIcon className={classes.infoIcon} />
        <Typography gutterBottom variant="h5" style={{ marginBottom: 24 }}>
          {statement.name}
        </Typography>
        <Typography gutterBottom variant="subtitle2">
          Topic idea and overview
        </Typography>
        <Typography variant="body2" color="textPrimary">
          {prompt.idea}
        </Typography>
        <br />
        <Typography gutterBottom variant="subtitle2">
          Task
        </Typography>
        <Typography variant="body2" color="textPrimary">
          {prompt.task}
        </Typography>
        <br />
        <Typography gutterBottom variant="subtitle2">
          Action
        </Typography>
        <Typography variant="body2" color="textPrimary">
          {prompt.action}
        </Typography>
        <br />
        <Typography gutterBottom variant="subtitle2">
          Result, achievement, outcome
        </Typography>
        <Typography variant="body2" color="textPrimary">
          {prompt.result}
        </Typography>
        <br />
        <Box
          textAlign="right"
          paddingTop={3}
          paddingBottom={3}
          onClick={onClose}
        >
          <Button variant="contained" color="primary">
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const StatementCard = ({
  data,
  onDelete,
  onEdit,
  onUpdate,
  confirm,
  onDeleteDoc,
  student,
  hasCCCredit,
  hasHCCredit,
  onViewLogs,
  readOnly,
  hasExpertCredit,
}) => {
  const [showDetails, toggleDetails] = useState(false);
  const [showAddDoc, setShowAddDoc] = useState();
  const popupState = usePopupState({ variant: 'popover', popupId: 'menu' });
  const user = useSelector((s) => s.user.user);
  const profile = useSelector((s) => s.student.profile);
  const classes = useStyles();


  const handleDelete = () => {
    popupState.close();
    confirm(() => {
      onDelete(data.id);
    });
  };

  const handleEdit = () => {
    popupState.close();
    onEdit(data);
  };

  const handleLogs = () => {
    popupState.close();
    onViewLogs(data);
  };

  const onChangeStatus = (val) => {
    const { status } = val;
    return onUpdate(data.id, {
      draft_due_date: data.draft_due_date,
      status,
    });
  };

  const onUpdateDoc = (doc) => {
    setShowAddDoc(doc);
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex">
            <Typography variant="subtitle1">{data.name}</Typography>{' '}
            {data.essay_prompt && (
              <IconButton size="small" onClick={() => toggleDetails(true)}>
                <LaunchIcon />
              </IconButton>
            )}
            <Box marginLeft="auto">
              <StatusSelect
                readOnly={readOnly}
                value={data.status}
                onChangeStatus={onChangeStatus}
                profile={profile}
                student={student}
                hasCCCredit={hasCCCredit}
                hasHCCredit={hasHCCredit}
                hasExpertCredit={hasExpertCredit}
                user={user}
              />
            </Box>
          </Box>
        }
        subheader={
          <Box display="flex">
            <Typography variant="caption">
              {moment.utc(data.due_date).format('[Due] MMMM DD, YYYY')}
            </Typography>
            <Box marginLeft="auto">
              <Typography variant="caption">
                {data.status === 'draft' && data.draft_due_date
                  ? moment
                    .utc(data.draft_due_date)
                    .format('[Draft Due] MMM DD, YYYY')
                  : null}
              </Typography>
            </Box>
          </Box>
        }
        action={
          <IconButton {...bindTrigger(popupState)}>
            {<MoreVertIcon />}
          </IconButton>
        }
      />
      <CardContent>
        <Typography gutterBottom variant="body2">
          Prompt
        </Typography>
        {data && (
          <Typography paragraph variant="caption">
            {PROMPTS_CATEGORIES.find((i) => i.id === data?.prompt_id)?.text}
          </Typography>
        )}
        <Divider />
        <br />
        <Typography sx={{ color: 'text.secondary' }} gutterBottom variant="body2">
          Topic
        </Typography>
        {data.essay_topic && (
          <Typography paragraph variant="caption">
            {data.essay_topic.idea}
          </Typography>
        )}
        <Divider />
        <br />
        <Typography gutterBottom variant="body2">
          Schools
        </Typography>
        {data.schools?.map((i) => (
          <MuiLink
            to={
              user.type === 'student'
                ? `/schools/list/${i.value}`
                : `/students/${student.user_id}/schools/list/${i.value}`
            }
            style={{ marginRight: 8 }}
            component={Link}
            key={i.value}
            variant="caption"
          >
            {i.label}
          </MuiLink>
        ))}
        <br />
        <Divider style={{ marginTop: "20px" }} />
      </CardContent>
      <CardContent>
        <Typography gutterBottom variant="body2">
          Essays
        </Typography>
        {data.documents.map((i) => (
          <Box
            key={i.id}
            marginBottom={0.5}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <DocumentItem
              doc={i.document}
              readOnly={readOnly}
              user={user}
              onDelete={onDeleteDoc}
              onUpdate={onUpdateDoc}
            />
          </Box>
        ))}
      </CardContent>
      <CardActions className={classes.actions}>
        {!readOnly && (
          <Button
            onClick={() => setShowAddDoc(true)}
            color="primary"
            variant="outlined"
          >
            Add Essay
          </Button>
        )}
      </CardActions>
      <Menu {...bindMenu(popupState)}>
        <MenuItem disabled={readOnly} onClick={handleEdit}>
          Edit
        </MenuItem>
        <MenuItem onClick={handleLogs}>Logs</MenuItem>
        <MenuItem disabled={readOnly} onClick={handleDelete}>
          Delete
        </MenuItem>
      </Menu>
      {showDetails && (
        <StatementDetails
          prompt={data.essay_prompt}
          statement={data}
          onClose={() => toggleDetails(false)}
        />
      )}
      {showAddDoc && (
        <AddDocModal
          statement={data}
          onClose={() => setShowAddDoc()}
          user={student}
          doc={showAddDoc}
        />
      )}

    </Card>
  );
};

export default withConfirm(StatementCard);
